import { useState, useCallback, useEffect } from "react";
import {
  Form,
  Button,
  Select,
  Input,
  Table,
  Modal,
  AutoComplete,
  message,
  Tooltip,
  Checkbox,
} from "antd";
import {
  UserAddOutlined,
  CloseOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import DataTotalDisplay from "../../component/data-total-display/data-total-display.component";

import UserAPI from "../../api/UserAPI";
import ResidentAPI from "../../api/ResidentAPI";
import { setUserAccount } from "../../redux/reducers/userAccountSlice";
import { setMenu } from "../../redux/reducers/navigationSlice";
import { sortByDate, sortAlphabetically } from "../../utils/tableUtils";

import "./user-maintenance.styles.css";
import FormButtonsWrap from "../../component/form-buttons-wrap/form-buttons-wrap.component";

const initialFormValues = {
  id: "",
  name: "",
  email: "",
  phoneNumber: "",
  password: "",
  resident: "",
  residentName: "",
  roleId: "",
  status: "active",
};

const initialFindFormValues = {
  name: "",
  email: "",
  phoneNumber: "",
  resident: "",
  roleId: "",
  status: "",
};

const UserMaintenancePage = () => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const dispatch = useDispatch();
  const token = useSelector((state) => state.userAccount.user.token);

  const [userListData, setUserListData] = useState({
    users: [],
    totalCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [formModalIsOpen, setFormModalIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [roleTypes, setRoleTypes] = useState([]);
  const [isSelected, setIsSelected] = useState(false);
  const [residentIsPrimary, setResidentIsPrimary] = useState(false);
  const [residentBuilding, setResidentBuilding] = useState("");
  const [residents, setResidents] = useState([]);
  const [isTypingNewPassword, setIsTypingNewPassword] = useState(false);

  const listColumns = [
    {
      title: "Id",
      dataIndex: "Id",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.Id - b.Id,
    },
    {
      title: "Usuario",
      dataIndex: "Name",
      sorter: (a, b) => sortAlphabetically(a, b, "Name"),
    },
    {
      title: "Correo",
      dataIndex: "Email",
      sorter: (a, b) => sortAlphabetically(a, b, "Email"),
    },
    {
      title: "Teléfono",
      dataIndex: "PhoneNumber",
    },
    { title: "Residente", dataIndex: "Resident" },
    {
      title: "Rol",
      dataIndex: "Role",
    },
    {
      title: "Creado",
      dataIndex: "CreatedDate",
      sorter: (a, b) => sortByDate(a, b, "CreatedDate"),
    },
    {
      title: "Actualizado",
      dataIndex: "UpdatedDate",
      sorter: (a, b) => sortByDate(a, b, "UpdatedDate"),
    },
    { title: "Estado", dataIndex: "Status" },
    {
      title: "Opción",
      dataIndex: "view",
      render: (_, { Id }) => (
        <>
          <Button
            onClick={() => {
              loadSelectedUser(Id);
              setIsSelected(true);
            }}
          >
            Seleccionar
          </Button>
        </>
      ),
    },
  ];

  /**
   * Realiza búsqueda dinámica del residente mientras se escribe el nombre.
   * @param {string} name Nombre del residente
   */
  const onChangeResidentName = async (name) => {
    try {
      const residentsResponse = await ResidentAPI.getResidentsByName(
        token,
        name
      );
      setResidents(
        residentsResponse.data.residents.map((resident) => ({
          label: resident.Name,
          value: resident.Id,
        }))
      );
    } catch (ex) {
      let message = "No se pudo cargar residentes. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  /**
   * Pasa el id del residente seleccionado al input correspondiente y deja en el input de búsqueda el label (Nombre de residente) seleccionado.
   * @param {*} id Id seleccionado
   */
  const onSelectResidentName = async (id) => {
    form.setFieldValue("residentId", id);
    const selectedLabel = residents.find(
      (resident) => resident.value === id
    ).label;
    form.setFieldValue("residentName", selectedLabel);
  };

  const loadInitialData = useCallback(async () => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await UserAPI.getUserList(token);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const userListData = response.data;
      setUserListData(userListData.data);
    } catch (ex) {
      console.error(ex.message);

      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  }, [messageApi, dispatch, token]);

  const loadUserList = async (activePage = 1, activePageSize = 10) => {
    messageApi.open({ type: "loading", content: "Cargando..." });

    try {
      const response = await UserAPI.getUserList(token, {
        page: activePage,
        pageSize: activePageSize,
        ...searchForm.getFieldsValue(),
      });

      setCurrentPage(activePage);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const userListData = response.data;
      setUserListData(userListData.data);
    } catch (ex) {
      console.error(ex.message);
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) dispatch(setUserAccount(null));
      }

      messageApi.open({ type: "error", content: message });
    } finally {
      messageApi.destroy();
    }
  };

  const loadUserFormData = useCallback(async () => {
    try {
      const response = await UserAPI.getUserRoles(token);
      setRoleTypes(response.data.roles);
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  }, [messageApi, dispatch, token]);

  useEffect(() => {
    loadInitialData();
    loadUserFormData();
    dispatch(setMenu("userMaintenance"));
  }, [loadInitialData, loadUserFormData, dispatch]);

  const onFinishFormUser = async (values) => {
    if (
      values.roleId === "" ||
      values.roleId === undefined ||
      values.status === "" ||
      values.status === undefined
    ) {
      messageApi.open({
        type: "warning",
        content: "Favor completar formulario.",
      });
      return;
    }

    setIsLoading(true);

    try {
      let response = null;

      if (isSelected) {
        response = await UserAPI.updateUser(token, {
          ...values,
          residentIsPrimary,
        });
      } else {
        response = await UserAPI.createUser(token, {
          ...values,
          residentIsPrimary,
        });
      }

      messageApi.open({
        type: response.data.success ? "success" : "error",
        content: response.data.message,
      });
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde. ";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      setIsLoading(false);
      setIsSelected(false);
      form.resetFields();
      setResidentIsPrimary(false);
      setResidentBuilding("");
    }
  };

  const loadSelectedUser = async (id) => {
    try {
      const response = await UserAPI.getUser(token, id);

      if (!response.data.success) {
        messageApi.open({
          type: "error",
          content: response.data.message,
        });
        return;
      }

      const {
        Id,
        Name,
        PhoneNumber,
        Email,
        ResidentId,
        Resident,
        ResidentIsPrimary,
        ResidentBuilding,
        RoleId,
        Status,
      } = response.data.user;

      setFormModalIsOpen(true);
      form.setFieldValue("id", Id);
      form.setFieldValue("name", Name);
      form.setFieldValue("email", Email);
      form.setFieldValue("phoneNumber", PhoneNumber);
      form.setFieldValue("residentId", ResidentId);
      form.setFieldValue("residentName", Resident);
      form.setFieldValue("roleId", RoleId);
      form.setFieldValue("status", Status);
      setResidentIsPrimary(ResidentIsPrimary === 1);
      setResidentBuilding(ResidentBuilding);
    } catch (ex) {
      let message = "Servicio no disponible, intente más tarde.";

      if (ex.code !== "ERR_NETWORK") {
        message = ex.response.data.message;

        if (ex.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    }
  };

  const onChangePasswordInput = (e) => {
    const { value } = e.target;

    setIsTypingNewPassword(value !== "");
  };

  const onClickDownloadButton = async () => {
    try {
      messageApi.open({ type: "loading", content: "Cargando..." });

      const response = await UserAPI.downloadUsers(token, {
        ...searchForm.getFieldsValue(),
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute(
        "download",
        `Usuarios ${moment().format("DD-MM-YYYY")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      console.error(err.message);
      let message = "Servicio no disponible, intente más tarde.";

      if (err.code !== "ERR_NETWORK") {
        message = err.response.data.message;

        if (err.response.status === 401) {
          dispatch(setUserAccount(null));
        }
      }

      messageApi.open({
        type: "error",
        content: message,
      });
    } finally {
      messageApi.destroy();
    }
  };

  return (
    <div className="user-maintenance">
      <>{messageContextHolder}</>
      <Modal
        forceRender
        centered
        open={formModalIsOpen}
        width={800}
        footer={null}
        onCancel={() => {
          setFormModalIsOpen(false);
          setIsSelected(false);
          loadUserList();
          form.resetFields();
          setResidentIsPrimary(false);
          setResidentBuilding("");
        }}
      >
        <Form
          className="save-user-form"
          initialValues={initialFormValues}
          form={form}
          onFinish={onFinishFormUser}
          labelCol={{
            span: 7,
          }}
          wrapperCol={{
            span: 14,
          }}
          layout="horizontal"
        >
          <h2 style={{ padding: "5px" }}>
            {isSelected ? "Editando" : "Crear usuario"}
          </h2>
          <div className="custom-form-row">
            <div className="custom-form-col">
              <h3>Usuario</h3>
              <Form.Item type="text" name="id" style={{ display: "none" }}>
                <Input maxLength={11} />
              </Form.Item>
              <Form.Item label="Usuario" name="name" required>
                <Input type="text" maxLength={64} required />
              </Form.Item>
              <Form.Item label="Email" name="email">
                <Input type="email" maxLength={200} />
              </Form.Item>
              <Form.Item label="Teléfono" name="phoneNumber" required>
                <Input type="text" maxLength={20} required />
              </Form.Item>
              <Form.Item
                label="Clave"
                name="password"
                rules={[
                  {
                    required:
                      !isSelected || (isSelected && isTypingNewPassword),
                    message: "Requerido",
                  },
                  {
                    min: 8,
                    message: "La clave debe tener al menos 8 dígitos.",
                  },
                ]}
              >
                <Input
                  type="password"
                  maxLength={64}
                  placeholder={isSelected ? "Escribir para cambiar" : ""}
                  onChange={onChangePasswordInput}
                />
              </Form.Item>
              <Form.Item label="Rol" name="roleId" required>
                <Select>
                  <Select.Option value="">-- Seleccionar --</Select.Option>
                  {roleTypes.map((role) => (
                    <Select.Option key={role.Id} value={role.Id}>
                      {role.RoleName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="Estado" name="status" required>
                <Select
                  options={[
                    { value: "inactive", label: "Inactivo" },
                    { value: "active", label: "Activo" },
                  ]}
                />
              </Form.Item>
            </div>
            <div className="custom-form-col">
              <h3>Información de Residente</h3>
              <Form.Item label="Residente" name="residentName">
                <AutoComplete
                  options={residents}
                  onChange={onChangeResidentName}
                  onSelect={onSelectResidentName}
                  suffixIcon={
                    <Tooltip title="Remover relación">
                      <CloseOutlined
                        onClick={() => form.setFieldValue("residentId", "")}
                      />
                    </Tooltip>
                  }
                />
              </Form.Item>
              <Form.Item label="ID Residente" name="residentId">
                <Input type="text" maxLength={20} disabled />
              </Form.Item>
              <Form.Item label="Primario" name="residentIsPrimary">
                <Checkbox
                  onChange={({ target }) => {
                    setResidentIsPrimary(target.checked);
                  }}
                  checked={residentIsPrimary}
                />
              </Form.Item>
              <Form.Item label="Apartamento">
                <label style={{ color: "blue" }}>{residentBuilding}</label>
              </Form.Item>
              <div className="buttons-container">
                <Button type="primary" htmlType="submit" loading={isLoading}>
                  GUARDAR
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </Modal>
      <div className="user-list-container">
        <h2>
          Usuarios{" "}
          <Button onClick={() => setFormModalIsOpen(true)} type="primary">
            <UserAddOutlined /> Nuevo
          </Button>
        </h2>
        <Form
          initialValues={initialFindFormValues}
          form={searchForm}
          onFinish={() => loadUserList()}
          layout="vertical"
          style={{ display: "flex", gap: "10px" }}
        >
          <Form.Item label="Usuario" name="name">
            <Input />
          </Form.Item>
          <Form.Item label="Correo" name="email">
            <Input />
          </Form.Item>
          <Form.Item label="Teléfono" name="phoneNumber">
            <Input />
          </Form.Item>
          <Form.Item label="Residente" name="resident">
            <Input />
          </Form.Item>
          <Form.Item label="Rol" name="roleId" style={{ width: "150px" }}>
            <Select>
              <Select.Option value="">-- Seleccionar --</Select.Option>
              {roleTypes.map((role) => (
                <Select.Option key={role.Id} value={role.Id}>
                  {role.RoleName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Estado" name="status" style={{ width: "150px" }}>
            <Select>
              <Select.Option value="">-- Seleccionar --</Select.Option>
              <Select.Option value="active">Activo</Select.Option>
              <Select.Option value="inactive">Inactivo</Select.Option>
            </Select>
          </Form.Item>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Button type="primary" htmlType="submit">
              Buscar
            </Button>
          </div>
          <FormButtonsWrap>
            <Button
              htmlType="button"
              onClick={onClickDownloadButton}
              icon={<DownloadOutlined />}
            >
              Descargar
            </Button>
            <Button
              onClick={() => {
                searchForm.resetFields();
                loadUserList();
              }}
            >
              Limpiar
            </Button>
          </FormButtonsWrap>
        </Form>
        <DataTotalDisplay
          currentCount={userListData.users.length}
          totalCount={userListData.totalCount}
        />
        <Table
          columns={listColumns}
          dataSource={userListData.users}
          size="small"
          pagination={{
            current: currentPage,
            pageSize: currentPageSize,
            total: userListData.totalCount,
          }}
          onChange={async (e) => {
            await loadUserList(e.current, e.pageSize);
            setCurrentPage(e.current);
            setCurrentPageSize(e.pageSize);
          }}
          scroll={{ y: "360px" }}
          style={{ height: "360px" }}
        ></Table>
      </div>
    </div>
  );
};

export default UserMaintenancePage;
